import React, { useState } from 'react';
import { Phone, Mail, Plus, Ban, CheckCircle2, MessageCircle, RotateCcw, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import { fmtDateTime } from './crmHelpers';

const LABELS = ['Titular', 'Familiar', 'Laboral', 'Garante', 'Otro'];

const statusText = (c) => {
  if (!c.is_valid) return `Baja${c.baja_at ? ` el ${fmtDateTime(c.baja_at)}` : ''}${c.note ? ` · ${c.note}` : ''}`;
  if (c.is_confirmed) return `Confirmado${c.confirmed_at ? ` el ${fmtDateTime(c.confirmed_at)}` : ''}${c.confirmed_by ? ` por ${c.confirmed_by}` : ''}`;
  return 'Sin confirmar';
};

const ContactRow = ({ debtorId, contact, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const patch = async (data, okMsg) => {
    setBusy(true);
    try { await crmService.updateContact(debtorId, contact.id, data); toast.success(okMsg); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); } finally { setBusy(false); }
  };
  const Icon = contact.type === 'phone' ? Phone : Mail;
  const digits = contact.value.replace(/\D/g, '');
  const tone = !contact.is_valid ? 'border-red-100 bg-red-50/50' : contact.is_confirmed ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white';
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${tone}`} data-testid={`crm-contact-${contact.id}`}>
      <Icon className={`w-4 h-4 shrink-0 ${!contact.is_valid ? 'text-red-400' : contact.is_confirmed ? 'text-emerald-600' : 'text-slate-500'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium flex items-center gap-1.5 ${contact.is_valid ? 'text-slate-900' : 'text-red-500 line-through'}`}>
          {contact.value}
          {contact.is_valid && contact.is_confirmed && <BadgeCheck className="w-4 h-4 text-emerald-600" data-testid={`crm-contact-confirmed-${contact.id}`} />}
        </p>
        <p className="text-[11px] text-slate-500 truncate">{contact.label} · {contact.source_label || 'Original'} · <span data-testid={`crm-contact-status-${contact.id}`}>{statusText(contact)}</span></p>
      </div>
      {contact.type === 'phone' && contact.is_valid && (
        <>
          <a href={`tel:${contact.value}`} className="text-slate-500 hover:text-sky-600" title="Llamar" data-testid={`crm-contact-call-${contact.id}`}><Phone className="w-4 h-4" /></a>
          <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-emerald-600" title="Abrir WhatsApp" data-testid={`crm-contact-wa-${contact.id}`}><MessageCircle className="w-4 h-4" /></a>
        </>
      )}
      {contact.type === 'email' && contact.is_valid && <a href={`mailto:${contact.value}`} className="text-slate-500 hover:text-amber-600" title="Escribir" data-testid={`crm-contact-mail-${contact.id}`}><Mail className="w-4 h-4" /></a>}
      {contact.is_valid && !contact.is_confirmed && (
        <button type="button" disabled={busy} onClick={() => patch({ is_confirmed: true }, 'Contacto confirmado')} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid={`crm-contact-confirm-${contact.id}`}><CheckCircle2 className="w-4 h-4" />Confirmar</button>
      )}
      {contact.is_valid && (
        <button type="button" disabled={busy} onClick={() => patch({ is_valid: false, note: 'Baja manual' }, 'Contacto dado de baja')} className="text-[11px] font-semibold text-slate-400 hover:text-red-600 inline-flex items-center gap-1" data-testid={`crm-contact-baja-${contact.id}`}><Ban className="w-4 h-4" />Dar de baja</button>
      )}
      {!contact.is_valid && (
        <button type="button" disabled={busy} onClick={() => patch({ is_valid: true }, 'Contacto reactivado')} className="text-[11px] font-semibold text-sky-700 hover:text-sky-800 inline-flex items-center gap-1" data-testid={`crm-contact-reactivar-${contact.id}`}><RotateCcw className="w-4 h-4" />Reactivar</button>
      )}
    </div>
  );
};

const CrmContacts = ({ debtorId, contacts, onChanged }) => {
  const [form, setForm] = useState({ type: 'phone', value: '', label: 'Titular' });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.value.trim()) return;
    setSaving(true);
    try {
      await crmService.addContact(debtorId, form);
      setForm({ type: 'phone', value: '', label: 'Titular' });
      toast.success('Contacto agregado');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar'); } finally { setSaving(false); }
  };

  const order = (a, b) => (b.is_valid - a.is_valid) || (b.is_confirmed - a.is_confirmed);
  const phones = contacts.filter((c) => c.type === 'phone').sort(order);
  const emails = contacts.filter((c) => c.type === 'email').sort(order);
  return (
    <div className="space-y-4" data-testid="crm-contacts">
      <p className="text-[11px] text-slate-500">Los contactos no se editan: confirmalos cuando el titular los valide o dalos de baja si no sirven. Los dados de baja se conservan como historial.</p>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Teléfonos ({phones.length})</p>
        <div className="space-y-2">{phones.length ? phones.map((c) => <ContactRow key={c.id} debtorId={debtorId} contact={c} onChanged={onChanged} />) : <p className="text-sm text-slate-400">Sin teléfonos</p>}</div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Emails ({emails.length})</p>
        <div className="space-y-2">{emails.length ? emails.map((c) => <ContactRow key={c.id} debtorId={debtorId} contact={c} onChanged={onChanged} />) : <p className="text-sm text-slate-400">Sin emails</p>}</div>
      </div>
      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <p className="text-xs font-semibold text-slate-600 mb-2">Agregar contacto</p>
        <div className="flex flex-wrap gap-2">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="h-9 w-28 text-xs" data-testid="crm-contact-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="phone">Teléfono</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
          </Select>
          <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === 'phone' ? '351 123 4567' : 'correo@ejemplo.com'} className="h-9 flex-1 min-w-[160px] text-sm" data-testid="crm-contact-value" />
          <Select value={form.label} onValueChange={(v) => setForm({ ...form, label: v })}>
            <SelectTrigger className="h-9 w-28 text-xs" data-testid="crm-contact-label"><SelectValue /></SelectTrigger>
            <SelectContent>{LABELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={add} disabled={saving || !form.value.trim()} className="h-9 bg-slate-900 hover:bg-slate-800" data-testid="crm-contact-add"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
        </div>
      </div>
    </div>
  );
};

export default CrmContacts;
